import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { TableCell } from '@mui/material';
import {
  Typography,
  TableWrapperFooter,
  TableIconCell,
  TableDots,
  Pagination,
  Button,
  enqueueSnackbar,
  assignYardsAndClientsModal,
  SimpleTable,
  Chip,
} from '../../../design-system';
import { DashboardHeader } from '../../dashboard';
import { getNotificationData } from '../../notifications';
import { Filters } from './Filters';
import {
  usePaginationState,
  useClientsQuery,
  useAssignYardsMutation,
  useStatesWithFilterCounter,
} from '../../../hooks';
import { formatPhoneNumber, showToRoles } from '../../../utils';
import { TABLE, MESSAGES, ClientCategoryToChipColors } from '../../../const';
import { i18n } from '../../../i18n';
import { useStoreUser } from '../../../context';

interface ClientsProps {
  innerScroll?: boolean;
}

export const ClientsPage = styled.div<ClientsProps>`
  display: grid;
  grid-template-rows: max-content 1fr;
  grid-row-gap: 3rem;

  height: 100%;

  ${(props) =>
    props.innerScroll &&
    css`
      overflow: hidden;
    `}
`;

export const ClientsHeader = ({ totalCount }: any) => {
  const { user } = useStoreUser();

  return (
    <DashboardHeader>
      <Typography variant={'textXL'} weight={'bold'}>
        {i18n.t('pages.clients.companiesTitle', { totalCount })}
      </Typography>
      {showToRoles(
        ['SUPER_ADMIN', 'YARD_COMPANY_ADMIN', 'YARD_MANAGER', 'YARD_DISPATCHER'],
        user,
      ) && (
        <Button size={'medium'} to={'/dashboard/clients/add'}>
          {i18n.t('pages.clientsAdd.title')}
        </Button>
      )}
    </DashboardHeader>
  );
};

export const ClientsBody = ({ setTotalCount }: any) => {
  const { user } = useStoreUser();

  const navigate = useNavigate();
  const { page, take, skip, handlePageChange } = usePaginationState();
  const { mutate } = useAssignYardsMutation();
  const [openDotsMenuIndex, setOpenMenuDotsSelect] = useState(null);
  const notificationData = useMemo(() => getNotificationData(), []);
  const [states, setStates, filteredStates] = useStatesWithFilterCounter(
    {
      clientId: '',
      clientName: '',
      category: 'All',
    },
    {
      ...(notificationData
        ? { clientId: notificationData.clientId, clientName: notificationData.clientName }
        : {}),
    },
  );

  const { data = { data: [], totalCount: 0 }, isFetching } = useClientsQuery({
    ...filteredStates,
    take,
    skip,
  });

  useEffect(() => {
    if (isFetching) return;

    setTotalCount(data.totalCount);
  }, [isFetching]);

  useEffect(() => {
    if (!notificationData) return;
    if (notificationData.clientId === states.clientId) return;

    setStates('force', {
      clientId: notificationData.clientId,
      clientName: notificationData.clientName,
    });
  }, [notificationData]);

  const handleFilterChange: typeof setStates = (...args) => {
    handlePageChange(null, 1);
    setStates(...args);
  };

  const handleDotsToggle = (i: any) => {
    if (openDotsMenuIndex !== i) return setOpenMenuDotsSelect(i);

    setOpenMenuDotsSelect(null);
  };

  const handleDotsOptionSelect = async (action: any, id: any) => {
    if (action !== 'add-yards' && action !== 'reactivate-yards' && action !== 'deactivate-yards')
      return;

    assignYardsAndClientsModal.id = id;
    assignYardsAndClientsModal.assignType = 'yards';
    assignYardsAndClientsModal.action = action;
    assignYardsAndClientsModal.fc = (props: any) =>
      mutate(
        { ...props, action },
        {
          onSuccess: () => {
            if (action === 'add-yards')
              enqueueSnackbar(MESSAGES.CLIENT.SUCCESS.ASSIGN_YARDS, 'success');
            if (action === 'reactivate-yards')
              enqueueSnackbar(MESSAGES.CLIENT.SUCCESS.REACTIVATED_YARDS, 'success');
            if (action === 'deactivate-yards')
              enqueueSnackbar(MESSAGES.CLIENT.SUCCESS.DEACTIVATED_YARDS, 'success');
          },
          onError: () => {
            if (action === 'add-yards')
              enqueueSnackbar(MESSAGES.CLIENT.ERROR.YARDS_NOT_ASSIGNED, 'error');
            if (action === 'add-yards')
              enqueueSnackbar(MESSAGES.CLIENT.ERROR.YARDS_NOT_REACTIVATED, 'error');
            if (action === 'add-yards')
              enqueueSnackbar(MESSAGES.CLIENT.ERROR.YARDS_NOT_DEACTIVATED, 'error');
          },
        },
      );
    assignYardsAndClientsModal.open();
  };

  const handleRowClick = (to: string, row: any) => {
    if (openDotsMenuIndex !== null) return;
    if (!row.referrerYardId) return;
    navigate(to);
  };

  return (
    <SimpleTable
      data={data}
      filters={() => <Filters setStates={handleFilterChange} {...states} />}
      tableRowProps={(row, i) => ({
        key: 'clients' + i,
        onClick: () => handleRowClick(`/dashboard/clients/${row.id}`, row),
      })}
      columns={[
        {
          column: 1,
          headText: () => i18n.t('label.actions'),
          body: (row, i) => (
            <TableIconCell component={'th'} align={'left'}>
              <TableDots
                open={openDotsMenuIndex === i}
                onToggle={() => handleDotsToggle(i)}
                options={[
                  { label: i18n.t('label.addYards'), value: 'add-yards' },
                  { label: i18n.t('label.reactivateYards'), value: 'reactivate-yards' },
                  { label: i18n.t('label.deactivateYards'), value: 'deactivate-yards' },
                ]}
                onSelect={(val: any) => {
                  handleDotsOptionSelect(val, row.id);
                }}
              />
            </TableIconCell>
          ),
          isHidden: () => !showToRoles(['SUPER_ADMIN'], user),
        },
        {
          headText: () => i18n.t('label.clientName'),
          body: (row) => (
            <TableCell component={'th'} align={'left'}>
              {row.legalName}
            </TableCell>
          ),
        },
        {
          headText: () => i18n.t('label.email'),
          bodyText: (row) => row.email,
        },
        {
          headText: () => i18n.t('label.phoneNumber'),
          bodyText: (row) => formatPhoneNumber(row.phoneNumber),
        },
        {
          headText: () => i18n.t('label.country'),
          bodyText: (row) => row.address.country,
        },
        {
          headText: () => i18n.t('label.city'),
          bodyText: (row) => row.address.city,
        },
        {
          headText: () => i18n.t('label.code'),
          bodyText: (row) => row.code,
        },
        {
          headText: () => i18n.t('label.category'),
          body: (row) => (
            <TableCell align={'left'}>
              <Chip
                size={'small'}
                label={row.category.toLowerCase()}
                color={(ClientCategoryToChipColors as any)[row.category]}
              />
            </TableCell>
          ),
        },
      ]}
      footer={() => (
        <TableWrapperFooter>
          <Pagination
            count={Math.ceil(data.totalCount / TABLE.PAGE_MAX_SIZE)}
            page={page}
            onChange={handlePageChange}
          />
        </TableWrapperFooter>
      )}
    />
  );
};
